// src/components/ScenarioSidebar/index.tsx
import { useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Tooltip, Switch } from '@mui/material';
import {
  Upload,
  FileText,
  CheckSquare,
  Square,
  Play,
  RefreshCw,
  ArrowRight,
  AlertCircle,
  Info,
  Trash2,
  Eye,
  X,
  Sparkles,
  Download,
  Lock,
} from 'lucide-react';
import type { Scenario, ContextFile } from 'types';
import type { SessionStatus } from 'scenarios';
import FilePreviewDialog from 'components/FilePreviewDialog';
import { downloadContextDocument } from 'services/filesApi';

interface ScenarioSidebarProps {
  scenario: Scenario;
  sessionStatus: SessionStatus;
  files: ContextFile[];
  /** Зашитые в сценарий документы — всегда в контексте, read-only (просмотр/скачивание) */
  scenarioFiles?: ContextFile[];
  selectedFileIds: string[];
  isFilesLoading: boolean;
  isUploading: boolean;
  uploadError?: string | null;
  deletingFileId?: string | null;
  onSelectFile: (fileId: string) => void;
  onDeselectFile: (fileId: string) => void;
  onUploadFiles: (files: File[]) => Promise<void>;
  onDeleteFile: (fileId: string) => Promise<void>;
  onRunScenario: () => Promise<void>;
  onResetSession: () => Promise<void>;
  /** Сброс во время первого запуска: прервать и вернуть к выбору файлов */
  onCancelRun?: () => Promise<void>;
  /** Режим «Глубокий анализ документов» */
  deepAnalysis?: boolean;
  onDeepAnalysisChange?: (value: boolean) => void;
}

/** Human-readable file count requirement */
function fileRequirementLabel(min: number, max: number): string {
  if (min === max) {
    if (min === 1) return 'Ровно 1 файл';
    return `Ровно ${min} файла`;
  }
  if (min === 1) return `От 1 до ${max} файлов`;
  return `От ${min} до ${max} файлов`;
}

/** Hint shown when not enough files are selected */
function selectionHint(selected: number, min: number, max: number): string | null {
  if (selected < min) {
    const need = min - selected;
    if (need === 1) return 'Выберите ещё 1 файл';
    return `Выберите ещё ${need} файла`;
  }
  if (selected < max) {
    const canAdd = max - selected;
    if (min === max) return null; // exact count, no hint needed
    if (canAdd === 1) return `Можно добавить ещё 1 файл`;
    return `Можно добавить ещё до ${canAdd} файлов`;
  }
  return null;
}

export default function ScenarioSidebar({
  scenario,
  sessionStatus,
  files,
  scenarioFiles = [],
  selectedFileIds,
  isFilesLoading,
  isUploading,
  uploadError,
  deletingFileId,
  onSelectFile,
  onDeselectFile,
  onUploadFiles,
  onDeleteFile,
  onRunScenario,
  onResetSession,
  onCancelRun,
  deepAnalysis = false,
  onDeepAnalysisChange,
}: ScenarioSidebarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewFile, setPreviewFile] = useState<ContextFile | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  // Счётчик глубины dragenter/dragleave — без него подсветка мигает при наведении
  // на дочерние элементы внутри зоны
  const dragDepth = useRef(0);
  const { minFiles, maxFiles } = scenario;

  const handleDragEnter = (e: React.DragEvent) => {
    if (isUploading || !e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragOver(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (isUploading || !e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (isUploading) return;
    e.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragOver(false);
    if (isUploading) return;
    const dropped = Array.from(e.dataTransfer.files ?? []);
    if (dropped.length > 0) void onUploadFiles(dropped);
  };

  const toggleFile = (fileId: string) => {
    if (selectedFileIds.includes(fileId)) {
      onDeselectFile(fileId);
    } else if (selectedFileIds.length < maxFiles) {
      onSelectFile(fileId);
    }
  };

  // Сколько файлов реально можно выбрать (ограничено maxFiles)
  const selectableCount = Math.min(files.length, maxFiles);
  // Все доступные слоты заняты — кнопка переключается в режим «Снять выделение»
  const allSelectableSelected = selectableCount > 0 && selectedFileIds.length >= selectableCount;

  const toggleSelectAll = () => {
    if (allSelectableSelected) {
      selectedFileIds.forEach((id) => onDeselectFile(id));
      return;
    }
    let added = selectedFileIds.length;
    for (const file of files) {
      if (added >= maxFiles) break;
      if (!selectedFileIds.includes(file.id)) {
        onSelectFile(file.id);
        added += 1;
      }
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    const files = list ? Array.from(list) : [];
    if (files.length > 0) void onUploadFiles(files);
    e.target.value = '';
  };

  const handleDownload = async (file: ContextFile) => {
    if (downloadingId) return;
    setDownloadingId(file.id);
    try {
      await downloadContextDocument(file.id, file.name);
    } catch {
      // Скачивание не критично — пользователь может открыть просмотр и скачать оттуда
    } finally {
      setDownloadingId(null);
    }
  };

  const selectedCount = selectedFileIds.length;
  const canRun = selectedCount >= minFiles && selectedCount <= maxFiles;
  const atMax = selectedCount >= maxFiles;
  const isRunning = sessionStatus === 'running';
  const isActive = sessionStatus === 'active';
  // Сессия идёт (сценарий запущен): показываем «активный» вид сразу при запуске,
  // а не в момент, когда допечатался первый ответ ИИ. Иначе сайдбар «перерисовывается» —
  // running и active дают один и тот же макет, поэтому смены вида в конце стрима нет.
  const inSession = isRunning || isActive;
  const hint = selectionHint(selectedCount, minFiles, maxFiles);
  const isUnderMin = selectedCount < minFiles && selectedCount > 0;

  // Блок зашитых в сценарий («статических») документов — read-only: просмотр + скачивание.
  // Они всегда участвуют в анализе, поэтому без чекбоксов и удаления. Рендерим и в idle,
  // и в активной сессии, чтобы состав контекста всегда был очевиден.
  const scenarioFilesBlock =
    scenarioFiles.length > 0 ? (
      <Box sx={{ flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.875 }}>
          <Lock size={10} style={{ flexShrink: 0, color: 'var(--color-text-muted)' }} />
          <Typography
            sx={{
              fontSize: 9.5,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'var(--color-text-muted)',
            }}
          >
            Файлы сценария ({scenarioFiles.length})
          </Typography>
          <Tooltip
            title="Эти документы зашиты в сценарий и всегда участвуют в анализе"
            placement="top"
            arrow
          >
            <Box sx={{ display: 'inline-flex', cursor: 'help' }}>
              <Info
                size={10}
                style={{ flexShrink: 0, color: 'var(--color-primary)', opacity: 0.7 }}
              />
            </Box>
          </Tooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            maxHeight: 168,
            overflowY: 'auto',
          }}
        >
          {scenarioFiles.map((file) => {
            const isDownloading = downloadingId === file.id;
            return (
              <Box
                key={file.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.875,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 'var(--radius-sm)',
                  bgcolor: 'var(--color-surface-subtle)',
                  border: '1px dashed var(--color-border)',
                  transition: 'all 0.12s',
                  '&:hover': {
                    borderColor: 'var(--color-border-hover)',
                    '& .scenario-file-action': { opacity: 1 },
                  },
                }}
              >
                <FileText size={12} style={{ flexShrink: 0, color: 'var(--color-text-muted)' }} />
                <Tooltip title={file.name} placement="top" enterDelay={400} arrow>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {file.name}
                  </Typography>
                </Tooltip>
                <Box
                  className="scenario-file-action"
                  onClick={() => setPreviewFile(file)}
                  title="Просмотреть"
                  sx={{
                    opacity: 0,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    borderRadius: 'var(--radius-xs)',
                    cursor: 'pointer',
                    color: 'var(--color-text-muted)',
                    transition: 'all 0.12s',
                    '&:hover': { bgcolor: 'rgba(20,116,255,0.08)', color: 'var(--color-primary)' },
                  }}
                >
                  <Eye size={12} />
                </Box>
                <Box
                  className="scenario-file-action"
                  onClick={() => !isDownloading && handleDownload(file)}
                  title="Скачать"
                  sx={{
                    opacity: 0,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    borderRadius: 'var(--radius-xs)',
                    cursor: isDownloading ? 'default' : 'pointer',
                    color: 'var(--color-text-muted)',
                    transition: 'all 0.12s',
                    '&:hover': { bgcolor: 'rgba(20,116,255,0.08)', color: 'var(--color-primary)' },
                  }}
                >
                  {isDownloading ? (
                    <CircularProgress size={10} sx={{ color: 'var(--color-text-muted)' }} />
                  ) : (
                    <Download size={12} />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    ) : null;

  return (
    <Box
      sx={{
        width: 252,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--color-border)',
        bgcolor: 'var(--color-surface)',
        overflow: 'hidden',
      }}
    >
      {/* Body — no outer scroll; only the file list scrolls */}
      <Box
        sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        {/* ── IDLE: files ── */}
        {!inSession && (
          <>
            {/* Files section — takes remaining height, file list scrolls inside.
                Также служит drop-зоной: файлы можно перетащить мышью, не только выбрать кнопкой */}
            <Box
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                position: 'relative',
                px: 2.25,
                pt: 1.75,
                pb: 1.5,
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Drag overlay */}
              {isDragOver && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 8,
                    zIndex: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    borderRadius: 'var(--radius-md)',
                    border: '2px dashed var(--color-primary)',
                    bgcolor: 'rgba(20,116,255,0.08)',
                    backdropFilter: 'blur(1px)',
                    pointerEvents: 'none',
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '50%',
                      bgcolor: 'rgba(20,116,255,0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Upload size={18} color="var(--color-primary)" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: 'var(--color-primary)',
                      textAlign: 'center',
                    }}
                  >
                    Отпустите файлы для загрузки
                  </Typography>
                </Box>
              )}
              {/* Files header: label + upload button */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.5,
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  Загруженные файлы
                </Typography>
                <Box
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.375,
                    fontSize: 10.5,
                    fontWeight: 600,
                    color: isUploading ? 'var(--color-text-muted)' : 'var(--color-primary)',
                    bgcolor: isUploading ? 'transparent' : 'rgba(20,116,255,0.06)',
                    border: '1px solid',
                    borderColor: isUploading ? 'var(--color-border)' : 'rgba(20,116,255,0.2)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': isUploading ? {} : { bgcolor: 'rgba(20,116,255,0.12)' },
                  }}
                >
                  {isUploading ? (
                    <CircularProgress size={10} sx={{ color: 'var(--color-text-muted)' }} />
                  ) : (
                    <Upload size={10} />
                  )}
                  {isUploading ? 'Загрузка...' : 'Загрузить'}
                </Box>
              </Box>
              {/* Upload error */}
              {uploadError && (
                <Box
                  sx={{
                    mt: 0.75,
                    mb: 0.5,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.625,
                    px: 1,
                    py: 0.75,
                    borderRadius: 'var(--radius-sm)',
                    bgcolor: 'rgba(239,68,68,0.06)',
                    border: '1px solid rgba(239,68,68,0.2)',
                  }}
                >
                  <AlertCircle
                    size={11}
                    style={{ flexShrink: 0, color: '#dc2626', marginTop: 1 }}
                  />
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 500,
                      color: '#dc2626',
                      lineHeight: 1.4,
                      wordBreak: 'break-word',
                    }}
                  >
                    {uploadError}
                  </Typography>
                </Box>
              )}

              {/* Requirement subtitle */}
              <Typography
                sx={{
                  fontSize: 10.5,
                  color: 'var(--color-text-muted)',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flexShrink: 0,
                }}
              >
                <Info
                  size={10}
                  style={{ flexShrink: 0, color: 'var(--color-primary)', opacity: 0.7 }}
                />
                {fileRequirementLabel(minFiles, maxFiles)}
              </Typography>

              {/* Selected count indicator */}
              {files.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.625,
                    mb: 1,
                    px: 1,
                    py: 0.625,
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 11,
                    flexShrink: 0,
                    ...(selectedCount >= minFiles
                      ? {
                          bgcolor: 'rgba(34,197,94,0.07)',
                          border: '1px solid rgba(34,197,94,0.2)',
                          color: '#16a34a',
                        }
                      : selectedCount > 0
                        ? {
                            bgcolor: 'rgba(245,158,11,0.07)',
                            border: '1px solid rgba(245,158,11,0.2)',
                            color: '#b45309',
                          }
                        : {
                            bgcolor: 'var(--color-bg)',
                            border: '1px solid var(--color-border)',
                            color: 'var(--color-text-muted)',
                          }),
                  }}
                >
                  {selectedCount >= minFiles ? (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: '#22c55e',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <AlertCircle size={10} style={{ flexShrink: 0 }} />
                  )}
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'inherit' }}>
                    {selectedCount === 0
                      ? 'Файлы не выбраны'
                      : `Выбрано: ${selectedCount} из ${minFiles === maxFiles ? minFiles : `${minFiles}–${maxFiles}`}`}
                  </Typography>
                </Box>
              )}

              {/* Bulk select toggle */}
              {files.length > 1 && maxFiles > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    mb: 1,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={toggleSelectAll}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 0.75,
                      py: 0.375,
                      border: 'none',
                      background: 'none',
                      borderRadius: 'var(--radius-xs)',
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--color-primary)',
                      cursor: 'pointer',
                      transition: 'background 0.12s',
                      '&:hover': { bgcolor: 'rgba(20,116,255,0.08)' },
                    }}
                  >
                    {allSelectableSelected ? (
                      <>
                        <Square size={12} style={{ flexShrink: 0 }} />
                        Снять выделение
                      </>
                    ) : (
                      <>
                        <CheckSquare size={12} style={{ flexShrink: 0 }} />
                        Выбрать все{files.length > maxFiles ? ` (${maxFiles})` : ''}
                      </>
                    )}
                  </Box>
                </Box>
              )}

              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                accept=".pdf,.docx,.doc,.txt,.xlsx,.csv"
                onChange={handleFileInput}
              />

              {isFilesLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'var(--color-text-muted)',
                    fontSize: 12,
                    flexShrink: 0,
                  }}
                >
                  <CircularProgress size={13} sx={{ color: 'var(--color-primary)' }} />
                  Загружаем список...
                </Box>
              ) : files.length === 0 ? (
                <Box
                  sx={{
                    border: '1.5px dashed rgba(20,116,255,0.25)',
                    borderRadius: 'var(--radius-md)',
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.75,
                    bgcolor: 'rgba(20,116,255,0.03)',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'rgba(20,116,255,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Upload size={14} color="var(--color-primary)" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      color: 'var(--color-text-muted)',
                      textAlign: 'center',
                      lineHeight: 1.45,
                    }}
                  >
                    Перетащите файлы сюда или загрузите, чтобы начать работу
                  </Typography>
                  <Box
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--color-primary)',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Выбрать файл <ArrowRight size={11} />
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                  }}
                >
                  {files.map((file) => {
                    const selected = selectedFileIds.includes(file.id);
                    const disabled = !selected && atMax;
                    const isDeleting = deletingFileId === file.id;
                    return (
                      <Box
                        key={file.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.875,
                          px: 1.25,
                          py: 0.875,
                          borderRadius: 'var(--radius-sm)',
                          transition: 'all 0.12s',
                          opacity: disabled || isDeleting ? 0.45 : 1,
                          bgcolor: selected ? 'rgba(20,116,255,0.07)' : 'transparent',
                          border: '1px solid',
                          borderColor: selected ? 'rgba(20,116,255,0.25)' : 'var(--color-border)',
                          '&:hover':
                            disabled || isDeleting
                              ? {}
                              : {
                                  bgcolor: selected ? 'rgba(20,116,255,0.10)' : 'var(--color-bg)',
                                  '& .delete-btn, & .view-btn': { opacity: 1 },
                                },
                        }}
                      >
                        <Box
                          onClick={() => !disabled && !isDeleting && toggleFile(file.id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.875,
                            flex: 1,
                            minWidth: 0,
                            cursor: disabled || isDeleting ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {selected ? (
                            <CheckSquare
                              size={14}
                              style={{ flexShrink: 0, color: 'var(--color-primary)' }}
                            />
                          ) : (
                            <Square
                              size={14}
                              style={{ flexShrink: 0, color: 'var(--color-text-muted)' }}
                            />
                          )}
                          <FileText
                            size={12}
                            style={{ flexShrink: 0, color: 'var(--color-text-muted)' }}
                          />
                          <Tooltip title={file.name} placement="top" enterDelay={400} arrow>
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: selected ? 600 : 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                color: selected
                                  ? 'var(--color-text-primary)'
                                  : 'var(--color-text-secondary)',
                              }}
                            >
                              {file.name}
                            </Typography>
                          </Tooltip>
                        </Box>
                        <Box
                          className="view-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewFile(file);
                          }}
                          title="Просмотреть"
                          sx={{
                            opacity: 0,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 20,
                            height: 20,
                            borderRadius: 'var(--radius-xs)',
                            cursor: 'pointer',
                            color: 'var(--color-text-muted)',
                            transition: 'all 0.12s',
                            '&:hover': {
                              bgcolor: 'rgba(20,116,255,0.08)',
                              color: 'var(--color-primary)',
                            },
                          }}
                        >
                          <Eye size={12} />
                        </Box>
                        <Box
                          className="delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDeleting) onDeleteFile(file.id);
                          }}
                          sx={{
                            opacity: 0,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 20,
                            height: 20,
                            borderRadius: 'var(--radius-xs)',
                            cursor: isDeleting ? 'not-allowed' : 'pointer',
                            color: 'var(--color-text-muted)',
                            transition: 'all 0.12s',
                            '&:hover': {
                              bgcolor: 'rgba(220,38,38,0.08)',
                              color: 'var(--color-error)',
                            },
                          }}
                        >
                          {isDeleting ? (
                            <CircularProgress size={10} sx={{ color: 'var(--color-text-muted)' }} />
                          ) : (
                            <Trash2 size={12} />
                          )}
                        </Box>
                      </Box>
                    );
                  })}

                  {/* Max reached hint */}
                  {atMax && maxFiles > 1 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.25,
                        fontSize: 10.5,
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      <Box
                        sx={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          bgcolor: '#6b7280',
                          flexShrink: 0,
                        }}
                      />
                      Достигнут максимум файлов
                    </Box>
                  )}
                </Box>
              )}

              {/* Under-min hint */}
              {hint && isUnderMin && (
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.625,
                    px: 1,
                    py: 0.625,
                    borderRadius: 'var(--radius-sm)',
                    bgcolor: 'rgba(245,158,11,0.07)',
                    border: '1px solid rgba(245,158,11,0.2)',
                    fontSize: 11,
                    color: '#b45309',
                    flexShrink: 0,
                  }}
                >
                  <AlertCircle size={11} style={{ flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'inherit' }}>
                    {hint}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Зашитые в сценарий файлы — read-only блок ниже зоны загрузки */}
            {scenarioFilesBlock && (
              <>
                <Box
                  sx={{ mx: 2.25, height: '1px', bgcolor: 'var(--color-border)', flexShrink: 0 }}
                />
                <Box sx={{ px: 2.25, pt: 1.5, pb: 1.75, flexShrink: 0 }}>{scenarioFilesBlock}</Box>
              </>
            )}
          </>
        )}

        {/* ── RUNNING / ACTIVE: session info ── */}
        {inSession && (
          <Box
            sx={{
              px: 2.25,
              pt: 2,
              pb: 1.5,
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(20,116,255,0.06)',
                border: '1px solid rgba(20,116,255,0.16)',
                borderRadius: 'var(--radius-md)',
                px: 1.75,
                py: 1.25,
                mb: 1.5,
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: '#22c55e',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--color-primary)',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Сценарий активен
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: 11.5, color: 'var(--color-text-muted)', lineHeight: 1.4 }}
              >
                Задавайте вопросы в чате — история сохраняется автоматически
              </Typography>
              {scenario.model && (
                <Box
                  sx={{
                    mt: 0.875,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.875,
                    py: '3px',
                    bgcolor: 'rgba(20,116,255,0.08)',
                    border: '1px solid rgba(20,116,255,0.18)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'var(--color-primary)',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {scenario.model.name}
                  </Typography>
                </Box>
              )}
            </Box>

            {selectedFileIds.length > 0 && (
              <>
                <Typography
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: 'var(--color-text-muted)',
                    mb: 0.875,
                    flexShrink: 0,
                  }}
                >
                  Файлы в работе ({selectedFileIds.length})
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    overflowY: 'auto',
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  {files
                    .filter((f) => selectedFileIds.includes(f.id))
                    .map((file) => (
                      <Box
                        key={file.id}
                        onClick={() => setPreviewFile(file)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.875,
                          px: 1.25,
                          py: 0.75,
                          borderRadius: 'var(--radius-sm)',
                          bgcolor: 'rgba(20,116,255,0.05)',
                          border: '1px solid rgba(20,116,255,0.15)',
                          cursor: 'pointer',
                          transition: 'background 0.12s',
                          '&:hover': {
                            bgcolor: 'rgba(20,116,255,0.1)',
                            '& .view-btn': { opacity: 1 },
                          },
                        }}
                      >
                        <FileText
                          size={12}
                          style={{ flexShrink: 0, color: 'var(--color-primary)' }}
                        />
                        <Tooltip title={file.name} placement="top" enterDelay={400} arrow>
                          <Typography
                            sx={{
                              fontSize: 11.5,
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                              color: 'var(--color-text-secondary)',
                            }}
                          >
                            {file.name}
                          </Typography>
                        </Tooltip>
                        <Eye
                          className="view-btn"
                          size={12}
                          style={{
                            flexShrink: 0,
                            opacity: 0,
                            color: 'var(--color-primary)',
                            transition: 'opacity 0.12s',
                          }}
                        />
                      </Box>
                    ))}
                </Box>
              </>
            )}

            {/* Зашитые в сценарий файлы — видны и во время активной сессии */}
            {scenarioFilesBlock && (
              <Box
                sx={{
                  mt: selectedFileIds.length > 0 ? 1.5 : 0,
                  pt: selectedFileIds.length > 0 ? 1.5 : 0,
                  borderTop: selectedFileIds.length > 0 ? '1px solid var(--color-border)' : 'none',
                }}
              >
                {scenarioFilesBlock}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* ── Bottom action button ── */}
      <Box sx={{ px: 2.25, py: 2, borderTop: '1px solid var(--color-border)', flexShrink: 0 }}>
        <Box
          role="switch"
          aria-checked={deepAnalysis}
          tabIndex={0}
          onClick={() => onDeepAnalysisChange?.(!deepAnalysis)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onDeepAnalysisChange?.(!deepAnalysis);
            }
          }}
          sx={{
            mb: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.875,
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'all 0.15s',
            border: '1.5px solid',
            borderColor: deepAnalysis ? 'var(--color-primary)' : 'var(--color-border)',
            bgcolor: deepAnalysis ? 'rgba(20,116,255,0.07)' : 'var(--color-surface-subtle)',
            '&:hover': {
              borderColor: deepAnalysis ? 'var(--color-primary)' : 'var(--color-border-hover)',
              bgcolor: deepAnalysis ? 'rgba(20,116,255,0.1)' : 'var(--color-surface-subtle)',
            },
          }}
        >
          <Sparkles
            size={15}
            strokeWidth={2.25}
            style={{
              color: deepAnalysis ? 'var(--color-primary)' : 'var(--color-text-muted)',
              flexShrink: 0,
              transition: 'color 0.15s',
            }}
          />
          <Typography
            sx={{
              flex: 1,
              fontSize: 12.5,
              fontWeight: deepAnalysis ? 600 : 500,
              color: deepAnalysis ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              transition: 'color 0.15s',
            }}
          >
            Глубокий анализ документов
          </Typography>
          <Switch
            size="small"
            checked={deepAnalysis}
            tabIndex={-1}
            sx={{
              ml: 'auto',
              pointerEvents: 'none',
              '& .MuiSwitch-track': {
                backgroundColor: '#64748b',
                opacity: 1,
                border: '1px solid #475569',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: 'var(--color-primary)',
                borderColor: 'var(--color-primary)',
                opacity: 1,
              },
              '& .MuiSwitch-thumb': {
                color: '#fff',
                boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
              },
            }}
          />
        </Box>
        {isRunning ? (
          <Box
            onClick={() => onCancelRun?.()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.875,
              width: '100%',
              py: 1,
              fontSize: 12.5,
              fontWeight: 600,
              color: '#b91c1c',
              bgcolor: 'rgba(239,68,68,0.06)',
              border: '1.5px solid rgba(239,68,68,0.25)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              transition: 'all 0.15s',
              '&:hover': {
                bgcolor: 'rgba(239,68,68,0.1)',
                borderColor: 'rgba(239,68,68,0.4)',
              },
            }}
          >
            <X size={13} />
            Сбросить
          </Box>
        ) : isActive ? (
          <Box
            onClick={onResetSession}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.875,
              width: '100%',
              py: 1,
              fontSize: 12.5,
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              bgcolor: 'var(--color-bg)',
              border: '1.5px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              transition: 'all 0.15s',
              '&:hover': {
                bgcolor: 'var(--color-surface-subtle)',
                borderColor: 'var(--color-border-hover)',
                color: 'var(--color-text-primary)',
              },
            }}
          >
            <RefreshCw size={13} />
            Новый сценарий
          </Box>
        ) : (
          <>
            <Box
              onClick={!isRunning && canRun ? onRunScenario : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.875,
                width: '100%',
                py: 1.125,
                fontSize: 13,
                fontWeight: 700,
                borderRadius: 'var(--radius-md)',
                cursor: isRunning || !canRun ? 'not-allowed' : 'pointer',
                transition: 'all 0.18s',
                ...(isRunning
                  ? {
                      bgcolor: 'rgba(20,116,255,0.12)',
                      border: '1.5px solid rgba(20,116,255,0.2)',
                      color: 'var(--color-primary)',
                    }
                  : canRun
                    ? {
                        background: 'linear-gradient(135deg, #0A45C0 0%, #1474FF 100%)',
                        color: '#fff',
                        border: '1.5px solid transparent',
                        boxShadow: '0 4px 16px rgba(20,116,255,0.35)',
                        '&:hover': {
                          boxShadow: '0 6px 20px rgba(20,116,255,0.45)',
                          transform: 'translateY(-1px)',
                        },
                      }
                    : {
                        bgcolor: 'var(--color-bg)',
                        border: '1.5px solid var(--color-border)',
                        color: 'var(--color-text-muted)',
                      }),
              }}
            >
              {isRunning ? (
                <>
                  <CircularProgress size={14} sx={{ color: 'var(--color-primary)' }} /> Запускаем...
                </>
              ) : (
                <>
                  <Play size={14} fill={canRun ? '#fff' : 'none'} /> Запустить сценарий
                </>
              )}
            </Box>

            {/* Contextual hint below button */}
            {!isRunning && (
              <Typography
                sx={{
                  mt: 0.875,
                  fontSize: 10.5,
                  textAlign: 'center',
                  color: canRun ? 'var(--color-primary)' : 'var(--color-text-muted)',
                  opacity: canRun ? 0.8 : 0.6,
                }}
              >
                {canRun
                  ? '↑ Готово к запуску'
                  : selectedCount === 0
                    ? `Нужно выбрать: ${fileRequirementLabel(minFiles, maxFiles).toLowerCase()}`
                    : (hint ?? '')}
              </Typography>
            )}
          </>
        )}
      </Box>

      <FilePreviewDialog
        open={!!previewFile}
        documentId={previewFile?.id ?? null}
        fileName={previewFile?.name ?? ''}
        mimeType={previewFile?.file?.mime_type ?? null}
        onClose={() => setPreviewFile(null)}
      />
    </Box>
  );
}
